import React, { useMemo } from 'react';
import { Button, Label, Tooltip } from '@patternfly/react-core';
import { AngleDownIcon, AngleRightIcon, ExternalLinkAltIcon } from '@patternfly/react-icons';
import { MatrixCell as MatrixCellType, MatrixRow as MatrixRowType } from '../../types/reports';
import { MatrixCell } from './MatrixCell';
import { aggregateCells, AggregatedColumn, formatTimeForReport } from '../../utils/reportHelpers';
import { getLabelColor, isValidJiraIssueKey } from '../../utils';
import { JIRA_BASE_URL } from '../../config';

interface MatrixRowProps {
  row: MatrixRowType;
  isExpanded?: boolean;
  aggregatedColumns?: AggregatedColumn[] | null;
  onToggle?: () => void;
  isGrandTotal?: boolean;
}


export const MatrixRow: React.FC<MatrixRowProps> = ({
  row,
  isExpanded = true,
  aggregatedColumns,
  onToggle,
  isGrandTotal = false,
}) => {
  const hasChildren = row.children && row.children.length > 0;

  // Format label as "Name (KEY)" when entityKey is available
  // Remove key prefix from label if present (e.g., "DSSTB - DSS Totalbet" -> "DSS Totalbet")
  const cleanLabel = row.entityKey && row.label.startsWith(`${row.entityKey} - `)
    ? row.label.slice(row.entityKey.length + 3)
    : row.label;
  const displayLabel = row.entityKey ? `${cleanLabel} (${row.entityKey})` : row.label;

  // Aggregate cells for weekly/monthly views
  const aggregatedCells = useMemo(() => {
    if (!aggregatedColumns) return null;
    return aggregateCells(row.cells, aggregatedColumns);
  }, [row.cells, aggregatedColumns]);

  const classNames = [
    'matrix-row',
    `matrix-row--depth-${row.depth}`,
    isGrandTotal ? 'matrix-row--grand-total' : '',
    `matrix-row--level-${row.level}`,
  ]
    .filter(Boolean)
    .join(' ');

  const isIssueWithValidKey = row.level === 'issue' && row.entityKey && isValidJiraIssueKey(row.entityKey);

  const labelContent = (
    <div className="matrix-row__label-content">
      {hasChildren && onToggle && (
        <Button variant="plain" onClick={onToggle} className="matrix-row__toggle" aria-label="Toggle row">
          {isExpanded ? <AngleDownIcon /> : <AngleRightIcon />}
        </Button>
      )}
      {!hasChildren && <span className="matrix-row__toggle-placeholder" />}
      {isIssueWithValidKey && (
        <Tooltip content="Open in Jira">
          <a
            href={`${JIRA_BASE_URL}/browse/${encodeURIComponent(row.entityKey!)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="matrix-row__jira-link"
            aria-label={`Open ${row.entityKey} in Jira`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLinkAltIcon />
          </a>
        </Tooltip>
      )}
      <span className="matrix-row__label-text">{displayLabel}</span>
    </div>
  );

  // Render cells - either aggregated or daily
  const renderCells = () => {
    if (aggregatedCells) {
      return aggregatedCells.map((cell) => (
        <div
          key={cell.key}
          className="matrix-cell matrix-cell--value matrix-cell--aggregated"
        >
          {formatTimeForReport(cell.totalSeconds)}
        </div>
      ));
    }
    return row.cells.map((cell) => (
      <MatrixCell key={cell.date} cell={cell} />
    ));
  };

  return (
    <div className={classNames}>
      <div className="matrix-cell matrix-cell--label">
        {displayLabel.length > 40 ? (
          <Tooltip content={displayLabel}>{labelContent}</Tooltip>
        ) : (
          labelContent
        )}
      </div>
      <div className="matrix-cell matrix-cell--labels">
        {row.labels && row.labels.length === 1 && (
          <Label isCompact color={getLabelColor(row.labels[0])}>{row.labels[0]}</Label>
        )}
        {row.labels && row.labels.length > 1 && (
          <Tooltip content={row.labels.map((lbl) => (
            <Label key={lbl} isCompact color={getLabelColor(lbl)} className="matrix-cell__label-tooltip-item">{lbl}</Label>
          ))}>
            <span className="matrix-cell__labels-count">{row.labels.length}</span>
          </Tooltip>
        )}
      </div>
      <div className="matrix-cell matrix-cell--total">
        <strong>{formatTimeForReport(row.totalSeconds)}</strong>
      </div>
      {renderCells()}
      <div className="matrix-cell matrix-cell--total">
        <strong>{formatTimeForReport(row.totalSeconds)}</strong>
      </div>
    </div>
  );
};

interface GrandTotalRowProps {
  cells: MatrixCellType[];
  aggregatedColumns?: AggregatedColumn[] | null;
  totalSeconds: number;
}

export const GrandTotalRow: React.FC<GrandTotalRowProps> = ({ cells, aggregatedColumns, totalSeconds }) => {
  // Aggregate cells for weekly/monthly views
  const aggregatedCells = useMemo(() => {
    if (!aggregatedColumns) return null;
    return aggregateCells(cells, aggregatedColumns);
  }, [cells, aggregatedColumns]);

  // Render cells - either aggregated or daily
  const renderCells = () => {
    if (aggregatedCells) {
      return aggregatedCells.map((cell) => (
        <div
          key={cell.key}
          className="matrix-cell matrix-cell--value matrix-cell--aggregated matrix-cell--grand-total"
        >
          <strong>{formatTimeForReport(cell.totalSeconds)}</strong>
        </div>
      ));
    }
    return cells.map((cell) => (
      <MatrixCell key={cell.date} cell={cell} isTotal />
    ));
  };

  return (
    <div className="matrix-row matrix-row--grand-total">
      <div className="matrix-cell matrix-cell--label">
        <strong>Grand Total</strong>
      </div>
      <div className="matrix-cell matrix-cell--labels" />
      <div className="matrix-cell matrix-cell--total">
        <strong>{formatTimeForReport(totalSeconds)}</strong>
      </div>
      {renderCells()}
      <div className="matrix-cell matrix-cell--total">
        <strong>{formatTimeForReport(totalSeconds)}</strong>
      </div>
    </div>
  );
};
